<template>
    <div class="order-service-action-buttons">
        <QuickPaymentSelector
            class="order-service-quick-payment-methods"
            v-if="showQuickPaymentMethods"
            :disabled="isPayImmediatelyOrderButtonDisabled"
            :quickPaymentMethods="cartPayment.operatorQuickPaymentMethods"
            @onSelected="onQuickPaymentMethodSelected"
        />

        <PaymentMethodSelector
            customClass="order-service-payment-method-selector"
            ref="paymentMethodSelectorComponent"
            v-model:selectedMethod="selectedPaymentMethod"
            :availableMethods="cartPayment.operatorPaymentMethods"
            :isLoading="cartPayment.isLoadingPaymentMethods"
            :disabled="isPaymentMethodsButtonDisabled"
            :useDiscountsPaymentMethod="isTotalFullyCoveredByDiscounts"
            :hasVouchers="cart.hasVouchers"
            @onSelected="onPaymentMethodSelected"
        >
            <template v-slot:header>
                <div class="tip-and-pay-form-container" v-if="showTip">
                    <strong>{{ translations.pay.enterTip }}</strong>
                    <TipAndPayForm
                        class="tip-and-pay-form"
                        v-model:tip="tip"
                        :totalConsumption="cart.totalAmount"
                        :disabled="payment.isProcessingPayment"
                    />
                </div>

                <div class="apply-discount-section-container" style="padding: 14px 24px 0">
                    <ApplyDiscountSection
                        :inOperatorApp="true"
                        class="operator"
                        :locationId="cart.locationId!"
                        v-if="useVouchersAndDiscounts"
                        @applyDiscountErrorOccurred="onApplyDiscountErrorOccurred"
                        @cartDiscountApplied="onCartDiscountApplied"
                        :cart-id="currentCartId"
                        :enableQrScanner="true"
                        :bill-id="undefined"
                    />
                </div>

                <div class="contact-data-section-container" style="padding: 14px 24px 0">
                    <ContactDataSection ref="contactDataSectionComponent" :isInOperatorApp="true" />
                </div>
                <div class="delivery-address-section-container" style="padding: 14px 24px 0">
                    <DeliveryAddressSection ref="deliveryAddressSectionComponent" :isInOperatorApp="true" />
                </div>
            </template>
        </PaymentMethodSelector>

        <ProceedButton
            class="create-order-button"
            :currentPrice="totalWithTip"
            :display-zero="cart.totalAmount != 0"
            :disabled="isPayImmediatelyOrderButtonDisabled"
            @click="onPayImmediatelyButtonClick"
        >
            <span v-if="!cartPayment.isProcessingPayment && !isPayLaterSelected">{{ translations.pay.payNow }}</span>
            <span v-if="!cartPayment.isProcessingPayment && isPayLaterSelected">{{ translations.cart.order }}</span>
            <span v-if="cartPayment.isProcessingPayment">{{ translations.general.addingButtonPlaceholder }}</span>
        </ProceedButton>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import type { IPaymentMethod } from "@/data/model/Customer";
    import type { ICartDiscountAppliedEventData } from "@/data/model/Voucher";
    import { onApplyDiscountErrorOccurred } from "@/data/services/DiscountsService";
    import {
        getIsApplyVouchersAllowedForOrderUseCase,
        getIsTipAllowed,
        isTipAllowedForService,
    } from "@/data/services/PaymentService";
    import { useCartStore } from "@/data/stores/cart";
    import { useCartPaymentStore } from "@/data/stores/cartPayment";
    import { useOfferStore } from "@/data/stores/offer";
    import { usePaymentStore } from "@/data/stores/payment";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, onMounted, ref, watch } from "vue";

    import ProceedButton from "@/components/elements/ProceedButton.vue";
    import ContactDataSection from "@/components/pay/ContactDataSection.vue";
    import DeliveryAddressSection from "@/components/pay/DeliveryAddress/DeliveryAddressSection.vue";
    import QuickPaymentSelector from "@/components/pay/QuickPaymentSelector.vue";
    import TipAndPayForm from "@/components/pay/TipAndPayForm.vue";
    import ApplyDiscountSection from "@/components/vouchers/ApplyDiscountSection.vue";
    import PaymentMethodSelector from "@/operator/components/payment/PaymentMethodSelector.vue";

    // stores
    const offer = useOfferStore();
    const cart = useCartStore();
    const cartPayment = useCartPaymentStore();
    const payment = usePaymentStore();
    const alloc = useTableAllocationStore();

    // payment methods selector actions
    const paymentMethodSelectorComponent = ref<InstanceType<typeof PaymentMethodSelector> | null>(null);
    const contactDataSectionComponent = ref<InstanceType<typeof ContactDataSection> | null>(null);
    const deliveryAddressSectionComponent = ref<InstanceType<typeof DeliveryAddressSection> | null>(null);
    const selectedPaymentMethod = ref(null as IPaymentMethod | null);
    const isSelectingPaymentMethod = computed(() => paymentMethodSelectorComponent.value?.isOpen ?? false);
    const isPaymentMethodSelected = computed(() => selectedPaymentMethod.value != null);
    const isPayLaterSelected = computed(() => selectedPaymentMethod.value?.type == payment.payLaterPaymentMethod.type);
    const isTipAllowed = computed(() => getIsTipAllowed(offer.orderService, selectedPaymentMethod.value));
    const showTip = computed(() => isTipAllowedForService(offer.orderService));
    const tip = ref<number | null>(null);
    const tipAmount = computed(() => (isTipAllowed.value ? (tip.value ?? 0) : 0));
    const totalWithTip = computed(
        () =>
            (isTipAllowed.value
                ? cart.totalAfterDiscountsAndVouchers + tipAmount.value
                : cart.totalAfterDiscountsAndVouchers) + cart.feesAmount
    );
    const isTotalFullyCoveredByDiscounts = computed(() => cart.isFullyCoveredByDiscounts && tipAmount.value == 0);

    // quick payment actions
    const hasQuickPaymentMethod = computed(() => cartPayment.operatorQuickPaymentMethods.length > 0);
    const showQuickPaymentMethods = computed(
        () => hasQuickPaymentMethod.value && !isSelectingPaymentMethod.value && !isTotalFullyCoveredByDiscounts.value
    );

    const currentCartId = computed(() => {
        return cart.currentCart?.cartId;
    });

    const useVouchersAndDiscounts = computed(() => {
        return (
            getIsApplyVouchersAllowedForOrderUseCase(selectedPaymentMethod.value, cart.orderService?.useCase) &&
            cart.totalCartMainItems > 0
        );
    });

    // pay button actions
    const isPaymentMethodsButtonDisabled = computed(() => cartPayment.isProcessingPayment);
    const isPayImmediatelyOrderButtonDisabled = computed(
        () => cart.totalCartMainItems == 0 || cartPayment.isProcessingPayment || isSelectingPaymentMethod.value
    );

    async function onCartDiscountApplied(eventData: ICartDiscountAppliedEventData) {
        cart.setCurrentCart(eventData.cart);
    }

    function checkTotalUnitsPerOrderLimits() {
        if (!cart.isMinimumTotalUnitsPerOrderReached()) {
            Popup.show({
                message: translations.cart.minimumTotalUnitsPerOrderNotReached.replace(
                    "{0}",
                    cart.getMissingUnitsToReachMinimumTotalUnitsPerOrder() + ""
                ),
                uniqueId: "minimumTotalUnitsPerOrderNotReached",
            });
            return false;
        }

        if (cart.isMaximumTotalUnitsPerOrderExceeded()) {
            Popup.show({
                message: translations.cart.maximumTotalUnitsPerOrderExceeded.replace(
                    "{0}",
                    cart.orderService?.useCase?.maximumTotalUnitsPerOrder + ""
                ),
                uniqueId: "maximumTotalUnitsPerOrderExceeded",
            });
            return false;
        }

        return true;
    }

    async function onPayImmediatelyButtonClick() {
        if (!checkTotalUnitsPerOrderLimits()) return;

        // if no payment method is selected - open payment method selector
        if (!isPaymentMethodSelected.value && paymentMethodSelectorComponent.value != null) {
            paymentMethodSelectorComponent.value.open();
        } else {
            await requestPayImmediatelly();
        }
    }

    async function onQuickPaymentMethodSelected(quickPaymentMethod: IPaymentMethod) {
        if (quickPaymentMethod != null && quickPaymentMethod.type === "OpenCashDrawer") {
            await sendOpenCashDrawerToPrinterCommand();
            paymentMethodSelectorComponent.value?.close();
            return;
        }

        if (!checkTotalUnitsPerOrderLimits()) return;

        if (!getIsTipAllowed(offer.orderService, quickPaymentMethod)) {
            resetTip();
        }

        selectedPaymentMethod.value = quickPaymentMethod;

        if (
            !getIsApplyVouchersAllowedForOrderUseCase(quickPaymentMethod, cart.orderService?.useCase) &&
            cart.hasVouchers
        ) {
            await cart.removeAllCartDiscounts();
        }

        await requestPayImmediatelly();
    }

    async function requestPayImmediatelly() {
        if (cartPayment.isEmailRequired) {
            const contactData = cart.currentCart!.contactDataEndUser;
            if (contactData?.email != null && contactData!.email.trim().length > 0) {
                showIsEmailCorrectPopup(contactData!.email);
                return;
            }
        }

        await payImmediatelly();
    }

    function showIsEmailCorrectPopup(email: string) {
        Popup.show({
            message: translations.pay.isEmailCorrectQuestion.replace("{0}", email),
            confirmHandler: () => {
                payImmediatelly();
            },
            confirmButton: translations.general.yes,
            cancelButton: translations.general.cancel,
            highlightCancel: true,
            confirmBtnOnTheLeft: false,
            uniqueId: "email-confirmation-popup",
        });
    }

    async function sendOpenCashDrawerToPrinterCommand() {
        try {
            const roomId = alloc.roomId;
            if (roomId == null) {
                throw new Error("Room id not specified. The command to send open cash drawer cannot be sent");
            }

            const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
            await operatorDao.sendOpenCashDrawerToPrinterCommand({ roomId: roomId });
        } catch (e) {
            logError(e);
            Popup.show({
                title: translations.general.error,
                message: translations.general.operationFailed,
            });
        }
    }

    async function onPaymentMethodSelected(paymentMethod: IPaymentMethod) {
        if (paymentMethod != null && paymentMethod.type === "OpenCashDrawer") {
            await sendOpenCashDrawerToPrinterCommand();
            paymentMethodSelectorComponent.value?.close();
            return;
        }

        if (!getIsApplyVouchersAllowedForOrderUseCase(paymentMethod, cart.orderService?.useCase) && cart.hasVouchers) {
            await cart.removeAllCartDiscounts();
        }

        // no need to validate contact data if no payment method is selected and there is no items in cart
        if (
            paymentMethod != null &&
            cart.hasItems &&
            (cartPayment.enabledFieldRequirementsCount > 0 || cartPayment.isRegisteredRequired)
        ) {
            const contactData = await contactDataSectionComponent.value!.validateAndCreateOrUpdateContactData(true);
            if (!contactData.isValid) {
                return;
            }
        }

        paymentMethodSelectorComponent.value?.close();
    }

    async function payImmediatelly() {
        const isSuccess = await cartPayment.payImmediatelly(
            cart.totalAmount,
            cart.totalDiscountsAndVouchersAmount,
            tipAmount.value,
            selectedPaymentMethod.value!
        );

        if (isSuccess) {
            resetTip();
        }
    }

    function resetTip() {
        tip.value = null;
    }

    onMounted(() => {
        cartPayment.tip = tipAmount.value;
    });

    watch(
        () => paymentMethodSelectorComponent.value?.isOpen,
        () => {
            if (!getIsTipAllowed(offer.orderService, selectedPaymentMethod.value) || tip.value === 0) {
                resetTip();
            }
        }
    );

    watch(
        () => offer.orderService,
        () => {
            paymentMethodSelectorComponent.value?.reset();
            resetTip();
        }
    );

    watch(
        () => tipAmount.value,
        () => {
            cartPayment.tip = tipAmount.value;
        }
    );
</script>

<style lang="scss">
    .order-service-action-buttons {
        display: flex;
        align-items: flex-start;

        .order-service-quick-payment-methods {
            padding: 0;
            margin-bottom: -8px;
            margin-right: 12px;
            padding-top: 5px;
            flex: 0 10 auto;

            & > .method {
                width: 64px;
                min-width: 64px;
                margin-right: 4px;
            }
        }

        .order-service-payment-method-selector {
            margin-left: -10px;
            flex: 0 0 auto;
        }

        .create-order-button {
            flex: 1 1 320px;
        }

        .apply-discount-section-container {
            padding: 23px 24px 1px;
        }

        .tip-and-pay-form-container {
            padding: 2em 1.75em 0;

            .tip-and-pay-form {
                align-items: flex-start;
                padding-left: 1.5em;
                padding-top: 0.5em;

                label {
                    text-align: left;
                }
            }
        }
    }
</style>
